"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { type RelatedVariant } from "@/lib/content-service/types/ContentServiceProduct"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getProductVariantLinkLabel } from "@/src/utils/product/getProductVariantLinkLabel"
import { t } from "@/translations"

type ProductCardSwatchesProps = {
    sortedSwatches: RelatedVariant[]
    langCode: string
    handleChangeVariant: (product?: RelatedVariant) => void
    showBenefits?: boolean
    handleGoToSwatchTracking: (swatch: RelatedVariant, swatchIndex: number) => void
}

export default function ProductCardSwatches({
    sortedSwatches,
    langCode,
    handleChangeVariant,
    showBenefits,
    handleGoToSwatchTracking
}: ProductCardSwatchesProps) {
    const displayedSwatches = sortedSwatches.slice(0, 6)
    const extraColorsCount = sortedSwatches.length > 6 ? sortedSwatches.length - 6 : 0

    return (
        <div className={`group mt-14 flex h-24 w-full px-2`}>
            <div
                className={`hidden w-full items-center gap-8 lg:group-hover:flex ${
                    showBenefits ? "flex-wrap" : ""
                }`}
            >
                <>
                    {displayedSwatches.map((swatch, index) => {
                        return (
                            <DynamicLink
                                href={`/product/${swatch.uri}`}
                                className={clsx(
                                    "w-24 cursor-pointer",
                                    FOCUS_VISIBLE_INSET_CLASS
                                )}
                                onMouseEnter={() => handleChangeVariant(swatch)}
                                onClick={() => handleGoToSwatchTracking(swatch, index)}
                                key={`${index}-${swatch?.uri ?? ""}`}
                                aria-label={getProductVariantLinkLabel(
                                    langCode,
                                    swatch.name,
                                    swatch.color_swatch?.color_name
                                )}
                            >
                                <div className="relative h-24 w-24 rotate-[-50deg]">
                                    {swatch.lens?.image ? (
                                        <BaseImage
                                            unoptimized
                                            alt=""
                                            src={swatch.lens.image.url}
                                            width={100}
                                            height={100}
                                            className="absolute inset-0 z-20 rounded-full"
                                            style={{
                                                clipPath:
                                                    "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="absolute inset-0 rounded-full"
                                            style={{
                                                clipPath:
                                                    "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                backgroundColor:
                                                    swatch.lens?.hex ??
                                                    swatch.color_swatch?.hex
                                            }}
                                        />
                                    )}
                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            clipPath:
                                                "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                            backgroundColor: swatch.color_swatch?.hex
                                        }}
                                    />
                                </div>
                            </DynamicLink>
                        )
                    })}

                    {extraColorsCount > 0 && (
                        <Text
                            size="small"
                            weight="regular"
                            className="whitespace-nowrap text-center text-main-dark-grey lg:text-16"
                        >
                            +{extraColorsCount} {t(langCode, "common.more")}
                        </Text>
                    )}
                </>
            </div>

            {displayedSwatches.length > 1 && (
                <Text
                    size="small"
                    weight="regular"
                    className={"text-main-dark-grey lg:text-16 lg:group-hover:hidden"}
                >
                    {sortedSwatches.length} {t(langCode, "product_page.colors")}
                </Text>
            )}
        </div>
    )
}
